import { createEntityAdapter } from '@reduxjs/toolkit'

import { soyshareApi } from '../../../../../services/api'
import { getGroups, isGroup } from '../helpers/groups'
import { savingMessagesNotification } from '../helpers/messages'

const adapter = createEntityAdapter({
  selectId: (formField) => formField.id,
  sortComparer: (a, b) => {
    const aFieldRow = a?.isGroup ? a.children[0]?.fieldRow : a.fieldRow
    const bFieldRow = b?.isGroup ? b.children[0]?.fieldRow : b.fieldRow

    if (!aFieldRow) return 1
    if (!bFieldRow) return -1

    return aFieldRow - bFieldRow
  }
})

const initialState = adapter.getInitialState()

const api = soyshareApi.injectEndpoints({
  endpoints: (build) => ({
    getFormFields: build.query({
      query: ({ formId }) => ({ url: `/form-fields/form-id/${formId}` }),
      providesTags: ['form-fields'],
      transformResponse: (response) => {
        const groups = getGroups(response)
        return adapter.setAll(initialState, [...response, ...groups])
      }
    }),

    createFormField: build.mutation({
      query: ({ formId, payload }) => ({
        url: `/form-fields/form-id/${formId}`,
        method: 'POST',
        body: payload
      }),
      onQueryStarted: async ({ formId }, { dispatch, queryFulfilled }) => {
        savingMessagesNotification.saving()
        const { data } = await queryFulfilled
        dispatch(api.util.updateQueryData('getFormFields', { formId }, (draft) => adapter.addOne(draft, data)))
        savingMessagesNotification.success()
      }
    }),

    updateFormField: build.mutation({
      query: ({ payload }) => ({
        url: `/form-fields/${payload.id}`,
        method: 'PATCH',
        body: payload
      }),
      onQueryStarted: async ({ payload, formId }, { dispatch, queryFulfilled }) => {
        savingMessagesNotification.saving()
        const patchResult = dispatch(
          api.util.updateQueryData('getFormFields', { formId }, (draft) =>
            adapter.updateOne(draft, {
              id: payload.id,
              changes: {
                ...payload,
                unsaved: false
              }
            })
          )
        )
        queryFulfilled.then(() => savingMessagesNotification.success()).catch(patchResult.undo)
      }
    }),

    removeFormField: build.mutation({
      query: ({ fieldId }) => ({
        url: `/form-fields/${fieldId}`,
        method: 'DELETE'
      }),
      onQueryStarted: async ({ fieldId, formId }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          api.util.updateQueryData('getFormFields', { formId }, (draft) => adapter.removeOne(draft, fieldId))
        )
        queryFulfilled.then(() => savingMessagesNotification.success()).catch(patchResult.undo)
      }
    }),

    updateFieldGroup: build.mutation({
      query: ({ payload }) => ({
        url: `/form-fields/rearrange`,
        method: 'PATCH',
        body: payload
      }),
      onQueryStarted: async ({ payload, formId, fieldId }, { dispatch, queryFulfilled }) => {
        savingMessagesNotification.saving()
        let updates = payload.map(({ id, ...changes }) => ({ id, changes: { ...changes, unsaved: false } }))
        updates.push({
          id: fieldId,
          changes: {
            unsaved: false,
            fieldGroup: payload[0].fieldGroup,
            groupType: payload[0].groupType
          }
        })
        const patchResult = dispatch(
          api.util.updateQueryData('getFormFields', { formId }, (draft) => adapter.updateMany(draft, updates))
        )
        queryFulfilled.then(() => savingMessagesNotification.success()).catch(patchResult.undo)
      }
    }),

    rearrangeFields: build.mutation({
      query: ({ payload }) => ({
        url: `/form-fields/rearrange`,
        method: 'PATCH',
        body: payload.filter(({ id }) => !isGroup(id))
      }),
      onQueryStarted: async ({ payload, formId }, { dispatch, queryFulfilled }) => {
        savingMessagesNotification.saving()
        const changes = payload.map(({ id, ...changes }) => ({ id, changes: { ...changes, unsaved: false } }))
        const patchResult = dispatch(
          api.util.updateQueryData('getFormFields', { formId }, (draft) => adapter.updateMany(draft, changes))
        )
        queryFulfilled.then(() => savingMessagesNotification.success()).catch(patchResult.undo)
      }
    }),

    duplicateField: build.mutation({
      query: ({ formId, payload }) => ({
        url: `/form-fields/duplicate/${formId}`,
        method: 'POST',
        body: payload
      }),
      onQueryStarted: async ({ formId }, { dispatch, queryFulfilled }) => {
        savingMessagesNotification.saving()
        const { data } = await queryFulfilled
        dispatch(api.util.updateQueryData('getFormFields', { formId }, (draft) => adapter.addMany(draft, data)))
        savingMessagesNotification.success()
      }
    })
  })
})

export const {
  useGetFormFieldsQuery,
  useCreateFormFieldMutation,
  useUpdateFormFieldMutation,
  useRemoveFormFieldMutation,
  useUpdateFieldGroupMutation,
  useRearrangeFieldsMutation,
  useDuplicateFieldMutation
} = api

// ACTIONS
const updateFields = (formId, updates) => (dispatch) => {
  const changes = updates.map(({ id, ...changes }) => ({ id, changes }))
  dispatch(api.util.updateQueryData('getFormFields', { formId }, (draft) => adapter.updateMany(draft, changes)))
}

const updateField = (formId, update) => (dispatch) => {
  const { id, ...changes } = update
  dispatch(api.util.updateQueryData('getFormFields', { formId }, (draft) => adapter.updateOne(draft, { id, changes })))
}

const addGroup = (formId, field) => (dispatch) => {
  dispatch(api.util.updateQueryData('getFormFields', { formId }, (draft) => adapter.addOne(draft, field)))
}

const removeGroup = (formId, fieldId) => (dispatch) => {
  dispatch(api.util.updateQueryData('getFormFields', { formId }, (draft) => adapter.removeOne(draft, fieldId)))
}

export const actions = { updateFields, updateField, addGroup, removeGroup }
